'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import type { Theme } from 'src/Theme';
import type { AskMeAnythingConfig } from './lib/config';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QUERY_KEYS } from 'src/constants/queries';
import { FlexRow } from 'src/components/Atoms/FlexBox';
import useAppStorage from 'src/hooks/useAppStorage';
import useFeatures from 'src/hooks/useFeatures';
import useLocale from 'src/hooks/useLocale';
import useTeams from 'src/hooks/useTeams';
import { useAiChatAuth } from 'src/hooks/useAiChatAuth';
import TokenPrompt from './components/TokenPrompt';
import { inspect, rejectionReason } from './lib/dev-credentials';
import { KNOWN_FEATURES } from 'src/constants/features';

// Mounted on every company page but rendered only once the feature and token checks pass,
// so the widget code (including motion) is fetched on demand instead of shipping with the layout.
const AskMeAnythingApiChat = dynamic(() => import('./AskMeAnythingApiChat'), {
  ssr: false
});
const AskMeAnythingDemoChat = dynamic(() => import('./AskMeAnythingDemoChat'), {
  ssr: false
});

/**
 * Decides where the chat's credential comes from, so neither the page that
 * mounts the widget nor the widget itself has to care.
 *
 * The chat backend will not take one-app's session token — it verifies `aud`
 * against `com.effektify.ai-chat`, while our `access_token` sits on the
 * `com.effektify.platform` axis — so `useAiChatAuth` exchanges the session for a
 * service-scoped one, the same way `useAdStudioAuth` does for ad-studio.
 *
 * Before that endpoint existed, developers pasted a token by hand. That path is
 * switched off rather than deleted: it is the only way to point the widget at a
 * backend the exchange cannot mint for (another environment, a local instance),
 * which is worth keeping while the chat service is still young.
 *
 * Annotated `boolean` on purpose. Left to infer, TypeScript narrows it to the
 * literal `false`, stops type-checking the pasted branch, and the code quietly
 * rots until someone flips it back.
 */
const USE_PASTED_TOKEN: boolean = false;

/** Dev-only, and deliberately not in `constants/session.ts`: it goes with the paste flow. */
const PASTED_TOKEN_KEY = 'ask_me_anything_token';
const CHAT_API_URL = process.env.NEXT_PUBLIC_CHAT_API_URL ?? '';

/**
 * BrightHaven HQ and friends — the sales demo teams.
 *
 * They have no connected sources, so the real assistant would answer every
 * question by saying there is no data, which is the opposite of what a demo is
 * for. These teams get the scripted transcript the widget shipped with
 * before the backend existed (see `useDemoChat`); every other team gets the API.
 */
const DEMO_TEAM_IDS = ['01K4VKTFG6NWGY98DV40949XBB',
// BrightHaven HQ
'01KZXMNTR6ETC4JG89MMDB041G',
// BrightHaven Local 1
'01KZXMPDK6MGZFJ31RRZGD9C9Q' // BrightHaven Local 2
];
const CredentialPill = styled(FlexRow)(({
  theme
}: {
  theme: Theme;
}) => ({
  position: 'fixed',
  bottom: '4.75rem',
  right: '5rem',
  zIndex: 9997,
  alignItems: 'center',
  gap: theme.sizes.spacing['04'],
  padding: `${theme.sizes.spacing['02']} ${theme.sizes.spacing['06']}`,
  borderRadius: theme.sizes.radius.full,
  border: `${theme.sizes.borderWidth[1]} solid ${theme.colors.border.secondary}`,
  backgroundColor: theme.colors.surface.primary,
  boxShadow: theme.boxShadows[1]
}));
const ChangeButton = styled('button')(({
  theme
}: {
  theme: Theme;
}) => ({
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  textDecoration: 'underline',
  color: theme.colors.text.tertiary,
  ...theme.typography['body-xs']
}));
const AskMeAnythingProvider: React.FC = () => {
  const $ = _c(39);
  const {
    currentTeam
  } = useTeams();
  const {
    locale
  } = useLocale();
  const {
    getItem,
    setItem,
    removeItem
  } = useAppStorage();
  const {
    isEnabled
  } = useFeatures();
  const {
    aiChatToken,
    aiChatTokenStale,
    refetchAiChatToken,
    aiChatAuthError,
    canPost
  } = useAiChatAuth();
  let t0;
  let t1;
  if ($[0] !== aiChatTokenStale || $[1] !== refetchAiChatToken) {
    t0 = () => {
      if (aiChatTokenStale) {
        refetchAiChatToken();
      }
    };
    t1 = [aiChatTokenStale, refetchAiChatToken];
    $[0] = aiChatTokenStale;
    $[1] = refetchAiChatToken;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== aiChatAuthError) {
    t2 = () => {
      if (aiChatAuthError) {
        console.warn("Ask me anything: could not authenticate with the chat service", aiChatAuthError);
      }
    };
    t3 = [aiChatAuthError];
    $[4] = aiChatAuthError;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const queryClient = useQueryClient();
  const teamKey = currentTeam?.id;
  let t4;
  if ($[7] !== queryClient || $[8] !== teamKey) {
    t4 = () => queryClient.getQueryData([QUERY_KEYS.AUTH.AI_CHAT, teamKey]) ?? "";
    $[7] = queryClient;
    $[8] = teamKey;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const serviceGetToken = t4;
  const [pastedToken, setPastedToken] = useState(null);
  const [restored, setRestored] = useState(!USE_PASTED_TOKEN);
  let t5;
  let t6;
  if ($[10] !== getItem || $[11] !== removeItem) {
    t5 = () => {
      if (!USE_PASTED_TOKEN) {
        return;
      }
      const stored = getItem(PASTED_TOKEN_KEY);
      if (stored && !rejectionReason(stored)) {
        setPastedToken(stored);
      } else {
        if (stored) {
          removeItem(PASTED_TOKEN_KEY);
        }
      }
      setRestored(true);
    };
    t6 = [getItem, removeItem];
    $[10] = getItem;
    $[11] = removeItem;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== setItem) {
    t7 = token => {
      setItem(PASTED_TOKEN_KEY, token);
      setPastedToken(token);
    };
    $[14] = setItem;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const acceptToken = t7;
  let t8;
  if ($[16] !== removeItem) {
    t8 = () => {
      removeItem(PASTED_TOKEN_KEY);
      setPastedToken(null);
    };
    $[16] = removeItem;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const forgetToken = t8;
  let t9;
  if ($[18] !== pastedToken) {
    t9 = pastedToken ? inspect(pastedToken)?.teams[0] : undefined;
    $[18] = pastedToken;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const pastedTeamId = t9;
  let t10;
  if ($[20] !== pastedToken) {
    t10 = () => pastedToken ?? "";
    $[20] = pastedToken;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const pastedGetToken = t10;
  const teamId = USE_PASTED_TOKEN ? pastedTeamId : currentTeam && `team:${currentTeam.id}`;
  let t11;
  bb0: {
    if (!teamId) {
      t11 = null;
      break bb0;
    }
    const t12 = USE_PASTED_TOKEN ? pastedGetToken : serviceGetToken;
    let t13;
    if ($[22] !== locale) {
      t13 = locale ? {
        locale
      } : {};
      $[22] = locale;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    let t14;
    if ($[24] !== t12 || $[25] !== t13 || $[26] !== teamId) {
      t14 = {
        apiUrl: CHAT_API_URL,
        getToken: t12,
        teamId,
        ...t13
      };
      $[24] = t12;
      $[25] = t13;
      $[26] = teamId;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    t11 = t14;
  }
  const config = t11;
  if (!isEnabled(KNOWN_FEATURES.ASK_ME_ANYTHING, false)) {
    return null;
  }
  if (currentTeam && DEMO_TEAM_IDS.includes(currentTeam.id)) {
    let t12;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <AskMeAnythingDemoChat />;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    return t12;
  }
  if (!CHAT_API_URL) {
    return null;
  }
  if (!restored) {
    return null;
  }
  if (USE_PASTED_TOKEN && !pastedToken) {
    if (!currentTeam) {
      return null;
    }
    let t12;
    if ($[29] !== acceptToken) {
      t12 = <TokenPrompt apiUrl={CHAT_API_URL} onToken={acceptToken} />;
      $[29] = acceptToken;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    return t12;
  }
  if (!USE_PASTED_TOKEN && (!aiChatToken || !canPost)) {
    return null;
  }
  if (!config) {
    return null;
  }
  let t12;
  if ($[31] !== forgetToken || $[32] !== pastedToken) {
    const info = pastedToken ? inspect(pastedToken) : undefined;
    t12 = USE_PASTED_TOKEN && info && <CredentialPill><Typography variant="body-xs" sx={{
        fontWeight: 500
      }}>{info.displayName ?? "pasted token"}</Typography><Typography variant="body-xs" sx={_temp}>{`${(info.teams[0] ?? "").replace("team:", "").slice(0, 6)}…`}</Typography><ChangeButton type="button" onClick={forgetToken}>change</ChangeButton></CredentialPill>;
    $[31] = forgetToken;
    $[32] = pastedToken;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== config) {
    t13 = <AskMeAnythingApiChat config={config} />;
    $[34] = config;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t12 || $[37] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
};
export default AskMeAnythingProvider;
function _temp(theme) {
  return {
    color: theme.colors.text.tertiary
  };
}